import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert } from "@mui/material";
import { useDispatch } from "react-redux";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Layout from "components/Screens/LoginV2/Layout/Layout";
import Text from "components/common/Text";
import Button from "components/common/Button";
import CustomAlert from "components/common/Alert";

import { ResetForgotPassword } from "store/actions/ForgotPassword";

import { forgotPasswordV2Api } from "services/auth";

import ForgotPasswordSchema from "validations/ForgotPasswordSchema";
import { getLayoutImageProps } from "components/Screens/LoginV2/Layout/utils";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { LOGIN_ROUTE } from "constants/routes";
import { FORGOT_PASSWORD_STEPS, screen1, screen2 } from "./constants";

import "./ForgotPassword.scss";

function ForgotPassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const userType = searchParams.get("userType") || "";

  const [alert, setAlert] = useState({ open: false, msg: "Something Went Wrong" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screen, setScreen] = useState(FORGOT_PASSWORD_STEPS[0]);

  const methods = useForm({
    defaultValues: {
      email: "",
    },
    mode: "onBlur",
    resolver: yupResolver(ForgotPasswordSchema),
  });

  const {
    handleSubmit,
    watch,
    reset,
    formState: { errors = {} },
    trigger,
  } = methods;

  const email = watch("email");

  const onSubmit = async (data) => {
    if (isSubmitting) return;

    const isEmailValid = await trigger("email");
    if (!isEmailValid) {
      setAlert({ open: true, msg: errors.email?.message });
      return;
    }

    setIsSubmitting(true);
    setAlert({ ...alert, open: false });
    const { ok, error } = await forgotPasswordV2Api({ email: data.email, type: userType });
    setIsSubmitting(false);
    if (!ok) {
      setAlert({ open: true, msg: error[0]?.msg || error[0]?.description || "Something Went Wrong" });
    } else {
      setScreen(FORGOT_PASSWORD_STEPS[1]);
    }
  };

  const redirectToLogin = () => {
    navigate(LOGIN_ROUTE);
  };

  useEffect(() => {
    dispatch(ResetForgotPassword({}));
    setScreen(FORGOT_PASSWORD_STEPS[0]);
    reset();
  }, [dispatch, reset]);

  const renderForgotPasswordEmailContent = () => (
    <div className="flex flex-col justify-center align-self-center h-full">
      <div className="heading-h1-semibold">Forgot your password?</div>
      <div className="para-body-m-regular color-text-body-primary mb-15">
        To reset your password, please enter the email address of your Edge account.{" "}
      </div>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Text
            name="email"
            type="email"
            label="Email address"
            labelClass="color-text-body-primary"
            fullWidth
            hideHelperText
            error={false}
            variant="outlined"
          />
          <span className="para-body-s-regular text-headings-secondary">We will email you a password reset link</span>

          {(alert.open || errors.email?.message) && (
            <Alert severity="error">
              <div className="text-error para-body-m-regular">
                {errors.email?.message || alert.msg || "Email address does not exist"}
              </div>
            </Alert>
          )}

          <div className="mt-1">
            <Button
              type="submit"
              id="reset-password"
              variant="contained"
              label="Reset my password"
              isLoading={isSubmitting}
              fullWidth
              size="large"
            />
          </div>
        </form>
      </FormProvider>
      <div className="mt-1">
        <Button
          type="submit"
          id="reset-password"
          variant="text"
          label="Return to login"
          fullWidth
          size="large"
          onClick={redirectToLogin}
        />
      </div>
    </div>
  );

  const renderDoneInstructionsSentScreen = () => (
    <div className="flex flex-col justify-center  align-self-center h-full done-instructions-sent-screen-container">
      <div className="heading-h1-semibold">Done, instructions sent!</div>
      <div className="mt-1">
        <CustomAlert
          severity="success"
          variant="standard"
          className="success-alert-container"
          icon={<CheckCircleOutlineIcon className="alert-icon-color" />}
          description={
            <div className=" para-body-m-regular color-text-success w-100p">
              We have sent an email to <span className="para-body-m-semibold">{email}</span> with password reset
              instructions.
            </div>
          }
        />
      </div>
      <div className="para-body-s-regular color-text-body-primary mt-075 mb-15">
        {" "}
        If the email does not show up soon, check your <span className="para-body-s-semibold">spam</span> folder.
      </div>

      <FormProvider {...methods}>
        <Button
          id="resend-email"
          variant="contained"
          label="Resend Email"
          fullWidth
          size="large"
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        />
      </FormProvider>

      <div className="mt-1">
        <Button
          type="submit"
          id="reset-password"
          variant="text"
          label="Return to login"
          fullWidth
          size="large"
          onClick={redirectToLogin}
        />
      </div>
    </div>
  );

  const renderContent = () => {
    switch (screen) {
      case screen1:
        return renderForgotPasswordEmailContent();
      case screen2:
        return renderDoneInstructionsSentScreen();
      default:
        return renderForgotPasswordEmailContent();
    }
  };

  return (
    <Layout contentContainerClass="forgot-password-container" {...getLayoutImageProps(userType)}>
      {renderContent()}
    </Layout>
  );
}

export default ForgotPassword;
